import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import {
  Check, X, Dumbbell, Brain, TrendingUp, Shield, Zap,
  ChevronRight, Loader2, Crown, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import { detectPlayBilling } from '@/utils/webviewDetect';
import { purchaseSubscription, getProductDetails, listExistingPurchases, formatPlayPrice } from '@/services/playBilling';
import { isNativePlatform } from '@/services/revenueCatService';
import { isWTNAvailable, purchaseSubscriptionWTN, listPurchasesWTN, getProductPriceWTN } from '@/services/webtonative';
import { trackPurchaseConversion } from '@/utils/gadsConversion';

const FEATURES = [
  { icon: Brain,       title: "Personal Trainer",     desc: "Adapts every session to your performance" },
  { icon: Dumbbell,    title: "Unlimited Workouts",      desc: "Full access to 2,400+ exercises with video" },
  { icon: TrendingUp,  title: "Smart Progression",       desc: "Auto-adjusts weight, sets & reps weekly" },
  { icon: Shield,      title: "Injury-Aware Training",   desc: "Exercises filtered for your safety" },
  { icon: Zap,         title: "Real-Time Coaching",      desc: "RPE tracking, fatigue monitoring, deload detection" },
];

// WTN product IDs — Google Play Billing purchases by subscription PRODUCT ID,
// not by base plan ID ('1m-bm' / '1-year-body-max-plan' are base plans under
// this product, per Play Console). WTN's inAppPurchase can't select a base
// plan, so both plans point at the same product until there are two separate
// Play Console products for monthly vs yearly.
const WTN_PRODUCT_IDS = {
  monthly: '1_m',
  yearly:  '1_y',
};

import { useCountryPrices } from '@/hooks/useCountryPrices';
import { CountrySheet } from '@/components/CountrySheet';
import { flagFor } from '@/utils/countryFlag';

export default function SubscriptionPage() {
  const { api, checkSubscription } = useAuth();
  const navigate = useNavigate();
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [isPlayBilling, setIsPlayBilling] = useState(false);
  const [playProduct, setPlayProduct] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('monthly');

  // Capture WTN availability into state — WTN is injected asynchronously in some
  // Android builds, so a synchronous inline check at click time can miss it and
  // fall through to Stripe. Poll briefly (same pattern as GoogleSignIn.jsx).
  const [isWTN, setIsWTN] = useState(() => isWTNAvailable());
  useEffect(() => {
    if (isWTN) return;
    const INTERVAL_MS = 200;
    const TIMEOUT_MS  = 5000;
    let elapsed = 0;
    const id = setInterval(() => {
      elapsed += INTERVAL_MS;
      if (isWTNAvailable()) {
        setIsWTN(true);
        clearInterval(id);
      } else if (elapsed >= TIMEOUT_MS) {
        clearInterval(id);
      }
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [isWTN]);

  // Detect Play Billing (TWA) availability on mount
  useEffect(() => {
    detectPlayBilling().then(available => {
      setIsPlayBilling(available);
      if (available) {
        getProductDetails().then(setPlayProduct);
      }
    });
  }, []);

  // ── Store pricing (Google Play builds only) ──
  // Inside the Play app the price must come from Play itself, in the user's own
  // currency. On the website we keep the standard prices below.
  const isStoreApp = isWTN || isPlayBilling;
  const [storePrices, setStorePrices] = useState({ monthly: null, yearly: null });
  useEffect(() => {
    if (!isStoreApp) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(['monthly', 'yearly'].map(async plan => {
        const productId = WTN_PRODUCT_IDS[plan];
        let price = null;
        let dgDetails = null;
        try {
          dgDetails = await getProductDetails(productId);
          price = formatPlayPrice(dgDetails?.price);
        } catch (e) { /* Digital Goods unavailable in this build */ }
        const bridgePrice = price ? null : await getProductPriceWTN(productId);
        if (!price) price = bridgePrice;
        console.log('[BODYMAX_STORE_PRICE]', JSON.stringify({
          plan,
          productId,
          wtnNativeApp: !!(window.WTN && window.WTN.isNativeApp),
          digitalGoodsApi: 'getDigitalGoodsService' in window,
          wtnPriceApis: window.WTN ? Object.keys(window.WTN).filter(k => /product|detail|sku|price/i.test(k)) : [],
          digitalGoodsDetails: dgDetails,
          bridgePrice,
          resolved: price,
        }));
        return [plan, price];
      }));
      if (!cancelled) setStorePrices(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [isStoreApp]);

  // ── Play Console price list, used when the bridge gives us no price ──
  // Google will not tell us the Play account's country, so we detect it (IP, then the
  // device time zone, then the language) and let the member change it. Shared with the
  // landing pages through useCountryPrices, so a choice in either place is remembered.
  const {
    countries: playCountries,
    country: storeCountry,
    setCountry: chooseCountry,
    row: countryRow,
    formatLocal,
  } = useCountryPrices({ enabled: isStoreApp, fetcher: () => api.get('/subscription/play-prices').then(r => r.data) });
  const [showCountrySheet, setShowCountrySheet] = useState(false);

  // Standard price on the web, the Play price in the app, and an honest
  // placeholder when neither Play nor a chosen country gives us one.
  const planPrice = (plan, webPrice) => {
    if (!isStoreApp) return { label: webPrice, isPrice: true };
    if (storePrices[plan]) return { label: storePrices[plan], isPrice: true };
    if (countryRow) return { label: formatLocal(countryRow[plan], countryRow.currency), isPrice: true };
    return { label: 'Price shown at checkout', isPrice: false };
  };

  // Trial badge is only shown to users who are actually eligible for the 7-day trial.
  const [trialEligible, setTrialEligible] = useState(false);
  useEffect(() => {
    api.get('/subscription/config')
      .then(res => setTrialEligible(!!res.data?.trial_eligible))
      .catch(() => setTrialEligible(false));
  }, [api]);

  // ── Access code state ──
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [redeemingCode, setRedeemingCode] = useState(false);
  const [codeError, setCodeError] = useState('');


  const handleSubscribe = async () => {
    setPurchasing(true);
    try {
      if (isWTN) {
        // ── WebToNative Native Purchase ──
        // Product IDs must match Play Console exactly.
        // basePlanId is NOT passed — WTN does not support it (not in official API spec).
        // Each product has one base plan; Play Billing resolves it from productId alone.
        // The free trial offer is presented automatically by Google Play to eligible users.
        const wtnProductId = WTN_PRODUCT_IDS[selectedPlan] || WTN_PRODUCT_IDS.monthly;
        const result = await purchaseSubscriptionWTN(wtnProductId);
        if (result.isSuccess) {
          // Validate with backend
          const validation = await api.post('/subscription/validate/google', {
            purchase_token: result.receiptData?.purchaseToken || result.receiptData,
            product_id: result.productId || wtnProductId,
            package_name: 'com.wnapp.id1780845607573',
            source: 'google_play',
          });
          if (validation.data.valid) {
            await checkSubscription();
            toast.success("Welcome to BODYMAX Premium!");
            navigate('/dashboard', { replace: true });
          } else {
            toast.error(validation.data.error || 'Purchase verification failed');
          }
        } else if (result.error === 'Purchase timed out' || result.error?.includes('cancel')) {
          // User cancelled — no toast
        } else {
          toast.error(result.error || 'Purchase failed. Try again.');
        }
      } else if (isPlayBilling) {
        // ── Google Play Billing (TWA only) ──
        const result = await purchaseSubscription();
        if (result.success) {
          const validation = await api.post('/subscription/validate/google', {
            purchase_token: result.purchaseToken,
            product_id: result.productId,
            package_name: 'com.wnapp.id1780845607573',
          });
          if (validation.data.valid) {
            await checkSubscription();
            toast.success("Welcome to BODYMAX Premium!");
            navigate('/dashboard', { replace: true });
          } else {
            toast.error(validation.data.error || 'Purchase verification failed');
          }
        } else if (result.error === 'cancelled') {
          // User cancelled
        } else {
          toast.error(result.error || 'Purchase failed. Try again.');
        }
      } else {
        // ── Stripe Checkout (web/PWA) ──
        const res = await api.post('/subscription/checkout', { plan_id: selectedPlan === 'yearly' ? 'premium_yearly' : 'premium_monthly', plan: selectedPlan });
        const { checkout_url } = res.data;
        if (checkout_url) {
          // Use assign for better mobile browser compatibility
          window.location.assign(checkout_url);
          return;
        } else {
          toast.error('Could not start checkout. Please try again.');
        }
      }
    } catch (err) {
      console.error('[BODYMAX_CHECKOUT] Error:', err);
      const msg = err.response?.data?.detail || err.message || 'Something went wrong';
      toast.error(msg);
    } finally {
      setPurchasing(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      if (isWTN) {
        const result = await listPurchasesWTN();
        if (result.isSuccess && result.purchases?.length > 0) {
          const purchase = result.purchases[0];
          const validation = await api.post('/subscription/validate/google', {
            purchase_token: purchase.purchaseToken,
            product_id: purchase.productId,
            package_name: 'com.wnapp.id1780845607573',
            source: 'google_play',
          });
          if (validation.data.valid) {
            await checkSubscription();
            toast.success('Subscription restored!');
            navigate('/dashboard', { replace: true });
          } else {
            toast.error('Could not verify purchase');
          }
        } else {
          toast.error('No active subscriptions found');
        }
      } else if (isPlayBilling) {
        const purchases = await listExistingPurchases();
        if (purchases.length > 0) {
          const validation = await api.post('/subscription/validate/google', {
            purchase_token: purchases[0].purchaseToken,
            product_id: purchases[0].itemId,
            package_name: 'com.wnapp.id1780845607573',
          });
          if (validation.data.valid) {
            await checkSubscription();
            toast.success('Subscription restored!');
            navigate('/dashboard', { replace: true });
            return;
          }
        }
        toast.info('No existing purchases found');
      } else {
        await checkSubscription();
        toast.info('Subscription status refreshed');
      }
    } catch {
      toast.error('Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const handleRedeemCode = async () => {
    const code = accessCode.trim();
    if (!code) return;

    setRedeemingCode(true);
    setCodeError('');
    try {
      await api.post('/subscription/redeem-access-code', { code });
      await checkSubscription();
      toast.success("Access activated!");
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid code';
      setCodeError(detail);
    } finally {
      setRedeemingCode(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="paywall-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2"
           style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
        <button
          onClick={() => navigate('/dashboard', { replace: true })}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-muted active:scale-95 transition-transform"
          data-testid="paywall-close-btn"
        >
          <X size={20} className="text-muted-foreground" />
        </button>
        {isNativePlatform() && (
          <button
            onClick={handleRestore}
            disabled={restoring}
            className="text-xs text-muted-foreground px-3 py-2 active:text-muted-foreground transition-colors"
            data-testid="paywall-restore-btn"
          >
            {restoring ? <Loader2 size={14} className="animate-spin" /> : 'Restore Purchase'}
          </button>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 pb-96">
        {/* Hero image banner */}
        <div className="relative rounded-2xl overflow-hidden mb-6 -mx-1 mt-2">
          <img
            src="https://images.unsplash.com/photo-1526409499567-9f0af1fcb6d1?w=800&h=400&fit=crop&crop=center"
            alt=""
            className="w-full h-44 object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h1 className="font-heading text-2xl font-black uppercase tracking-tight text-white mb-1">
              Unlock BODYMAX
            </h1>
            <p className="text-sm text-white/70">
              Your personal trainer. Smarter every session.
            </p>
          </div>
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="space-y-3 mb-8"
        >
          {FEATURES.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.06 }}
              className="flex items-center gap-4 p-3.5 rounded-xl bg-muted/30 border border-border"
            >
              <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                <f.icon size={18} className="text-accent" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{f.title}</p>
                <p className="text-xs text-muted-foreground">{f.desc}</p>
              </div>
              <Check size={16} className="text-accent/60 shrink-0 ml-auto" />
            </motion.div>
          ))}
        </motion.div>

        {/* Social proof */}
        <p className="text-xs text-muted-foreground text-center mb-4">Trusted by athletes worldwide</p>
      </div>

      {/* Fixed bottom CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background/95 to-transparent pt-6 px-5"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Plan selector */}
        <div className="flex gap-2 mb-4">
          {/* Yearly — recommended */}
          <button
            onClick={() => setSelectedPlan('yearly')}
            className={`flex-1 relative overflow-hidden rounded-xl p-3 text-left transition-all ${
              selectedPlan === 'yearly'
                ? 'border-2 border-accent bg-[#1a2a0a]'
                : 'border border-white/15 bg-[#141414]'
            }`}
            data-testid="plan-yearly"
          >
            {selectedPlan === 'yearly' && (
              <span className="absolute top-0 right-0 px-2 py-0.5 bg-accent text-accent-foreground text-[9px] font-heading font-black uppercase rounded-bl-lg">Best Value</span>
            )}
            <div className="text-xs font-heading font-bold uppercase tracking-wider text-white/60 mb-1">Yearly</div>
            {(() => {
              const { label, isPrice } = planPrice('yearly', '€99');
              return (
                <div className="flex items-baseline gap-1">
                  <span className={`font-heading font-black text-white ${isPrice ? 'text-2xl' : 'text-sm leading-tight'}`}
                        data-testid="plan-yearly-price">{label}</span>
                  {isPrice && <span className="text-xs text-white/50">/year</span>}
                </div>
              );
            })()}
            <div className="flex items-center gap-1.5 mt-1">
              {!isStoreApp && <span className="text-xs text-white/40 line-through">€120</span>}
              <span className="text-xs font-bold text-accent">Save 15%</span>
            </div>
          </button>
          
          {/* Monthly */}
          <button
            onClick={() => setSelectedPlan('monthly')}
            className={`flex-1 rounded-xl p-3 text-left transition-all ${
              selectedPlan === 'monthly'
                ? 'border-2 border-accent bg-[#1a2a0a]'
                : 'border border-white/15 bg-[#141414]'
            }`}
            data-testid="plan-monthly"
          >
            <div className="text-xs font-heading font-bold uppercase tracking-wider text-white/60 mb-1">Monthly</div>
            {(() => {
              const { label, isPrice } = planPrice('monthly', '€9.99');
              return (
                <div className="flex items-baseline gap-1">
                  <span className={`font-heading font-black text-white ${isPrice ? 'text-2xl' : 'text-sm leading-tight'}`}
                        data-testid="plan-monthly-price">{label}</span>
                  {isPrice && <span className="text-xs text-white/50">/mo</span>}
                </div>
              );
            })()}
            <div className="mt-1">
              <span className="text-xs text-white/40">Cancel anytime</span>
            </div>
          </button>
        </div>

        {/* Country line — Play builds only, so prices can be shown before checkout */}
        {isStoreApp && (
          <button
            onClick={() => setShowCountrySheet(true)}
            className="w-full mb-3 -mt-1 text-center text-xs text-white/40 underline underline-offset-2"
            data-testid="store-country-btn"
          >
            {storePrices.monthly || storePrices.yearly
              ? 'Prices from Google Play'
              : countryRow
                ? <>{flagFor(countryRow.code)} Prices for {countryRow.name} · Change</>
                : 'Choose your country to see prices'}
          </button>
        )}

        {/* Trial badge — only for trial-eligible users */}
        {trialEligible && (
        <div className="text-center mb-3" data-testid="trial-badge">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/30">
            <span className="text-accent text-sm font-bold">7-DAY FREE TRIAL</span>
          </div>
        </div>
        )}

        {/* Subscribe button */}
        <button
          onClick={handleSubscribe}
          disabled={purchasing}
          className="w-full h-14 rounded-2xl bg-accent text-accent-foreground font-heading font-bold uppercase tracking-wider text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-all duration-150 disabled:opacity-50"
          data-testid="paywall-subscribe-btn"
        >
          {purchasing ? (
            <><Loader2 size={20} className="animate-spin" /> Processing...</>
          ) : (
            <>Start Training <ChevronRight size={18} /></>
          )}
        </button>

        {/* Access code toggle */}
        {!showAccessCode ? (
          <button
            onClick={() => setShowAccessCode(true)}
            className="w-full mt-3 py-2 text-xs text-muted-foreground flex items-center justify-center gap-1.5 active:text-muted-foreground transition-colors"
            data-testid="access-code-toggle"
          >
            <KeyRound size={12} /> Have an access code?
          </button>
        ) : (
          <div className="mt-3" data-testid="access-code-form">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={accessCode}
                onChange={e => { setAccessCode(e.target.value.toUpperCase()); setCodeError(''); }}
                placeholder="Enter access code"
                className="flex-1 h-11 px-4 rounded-xl bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent/30"
                autoCapitalize="characters"
                autoComplete="off"
                data-testid="access-code-input"
              />
              <button
                onClick={handleRedeemCode}
                disabled={redeemingCode || !accessCode.trim()}
                className="h-11 px-5 rounded-xl bg-white text-black text-sm font-semibold active:scale-95 transition-transform disabled:opacity-40"
                data-testid="access-code-redeem-btn"
              >
                {redeemingCode ? <Loader2 size={16} className="animate-spin" /> : 'Redeem'}
              </button>
            </div>
            {codeError && (
              <p className="text-xs text-red-400 mt-1.5 px-1" data-testid="access-code-error">{codeError}</p>
            )}
          </div>
        )}

        {/* Legal links */}
        <div className="flex items-center justify-center gap-4 mt-3">
          <a href="/privacy" className="text-xs text-muted-foreground underline">Privacy Policy</a>
          <a href="/terms" className="text-xs text-muted-foreground underline">Terms of Service</a>
        </div>
      </div>

      {/* Country picker — shared with the landing pages */}
      <CountrySheet
        open={showCountrySheet}
        onClose={() => setShowCountrySheet(false)}
        countries={playCountries}
        selected={storeCountry}
        onSelect={chooseCountry}
      />

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUCCESS PAGE — handles Stripe Checkout return
// ═══════════════════════════════════════════════════════════════

export function SubscriptionSuccess() {
  const { api, checkSubscription } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    // If backend redirect included a fresh token (Instagram/Facebook fix),
    // persist it immediately so the user is re-authenticated
    const urlToken = searchParams.get('token');
    if (urlToken) {
      localStorage.setItem('bodymax-token', urlToken);
      sessionStorage.setItem('bodymax-token', urlToken);
      document.cookie = `bm_token=${urlToken};path=/;max-age=${7*86400};SameSite=Lax;Secure`;
      // Clean token from URL to avoid leaking in referrer headers
      window.history.replaceState({}, '', window.location.pathname + '?session_id=' + (searchParams.get('session_id') || ''));
    }

    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setVerifying(false);
      setVerified(true);
      return;
    }

    const verify = async () => {
      try {
        const res = await api.get(`/subscription/checkout/status/${sessionId}`);
        const { status, payment_status, amount, currency } = res.data;
        if (status === 'complete' && (payment_status === 'paid' || payment_status === 'no_payment_required')) {
          await checkSubscription();
          setVerified(true);
          // Fire Google Ads purchase conversion (idempotent — safe on refresh/remount)
          trackPurchaseConversion(sessionId, amount, currency);
        } else {
          toast.error('Payment was not completed. Please try again.');
          navigate('/subscribe', { replace: true });
        }
      } catch (err) {
        await checkSubscription();
        setVerified(true);
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [searchParams, api, checkSubscription, navigate]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-accent mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center">
        <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
          <Check size={36} className="text-accent" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-foreground mb-2">You're In!</h1>
        <p className="text-muted-foreground text-sm mb-8">Your premium access is active. Let's train.</p>
        <button
          onClick={() => navigate('/dashboard', { replace: true })}
          className="w-full h-12 rounded-2xl bg-accent text-accent-foreground font-bold active:scale-[0.98] transition-transform"
          data-testid="sub-success-btn"
        >
          Go to Training Hub
        </button>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CANCEL PAGE — user cancelled Stripe Checkout
// ═══════════════════════════════════════════════════════════════

export function SubscriptionCancel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      localStorage.setItem('bodymax-token', urlToken);
      sessionStorage.setItem('bodymax-token', urlToken);
      const secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `bm_token=${urlToken};path=/;max-age=${7*86400};SameSite=Lax${secure}`;
      window.history.replaceState({}, '', '/subscription/cancel');
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center max-w-sm mx-auto">
        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
          <span className="text-3xl">💪</span>
        </div>
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-white mb-3">Your Plan Is Waiting</h1>
        <p className="text-base text-white/60 mb-2">Your personalized program is ready to go — built around your goals, equipment, and schedule.</p>
        <p className="text-sm text-white/40 mb-8">Unlock full access to start training with your coach today.</p>
        
        <div className="space-y-3">
          <button
            onClick={() => navigate('/subscribe')}
            className="w-full py-4 rounded-xl font-heading font-bold uppercase text-sm tracking-wider text-black active:scale-[0.97] transition-transform"
            style={{ background: 'var(--accent-hex)' }}
            data-testid="cancel-subscribe-btn"
          >
            Unlock My Program
          </button>
          <button
            onClick={() => navigate('/workout')}
            className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white/50 font-heading font-bold uppercase text-xs tracking-wider active:scale-[0.97] transition-transform"
          >
            Maybe Later
          </button>
        </div>
        
        <div className="mt-8 space-y-2">
          <p className="text-xs text-white/30 font-heading uppercase tracking-wider">What you get</p>
          <div className="grid grid-cols-1 gap-2 text-left">
            {['Personalized weekly programs', 'Smart Coach — adapts every session', '2,200+ guided exercise demos', 'Progress tracking & insights'].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/50">
                <div className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                {f}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
